import * as Sentry from '@sentry/nextjs'
import { type ClassValue, clsx } from 'clsx'
import { format } from 'date-fns'
import countries from 'i18n-iso-countries'
import sk from 'i18n-iso-countries/langs/sk.json'
import { twMerge } from 'tailwind-merge'

import { IEmployee, IEmployeeReduced } from '@/_types/employee'

import { normalizeChainedHashFragment } from './next-link-href'

// Registrujeme slovenský jazyk pre krajiny
countries.registerLocale(sk)

// Štáty Európskej únie s ich ISO kódmi
const EU_COUNTRIES = [
	'BE', // Belgicko
	'BG', // Bulharsko
	'CY', // Cyprus
	'CZ', // Česko
	'DK', // Dánsko
	'EE', // Estónsko
	'FI', // Fínsko
	'FR', // Francúzsko
	'GR', // Grécko
	'HR', // Chorvátsko
	'HU', // Maďarsko
	'CH', // Švajčiarsko
	'IE', // Írsko
	'IT', // Taliansko
	'LT', // Litva
	'LU', // Luxembursko
	'LV', // Lotyšsko
	'MT', // Malta
	'NL', // Holandsko
	'PL', // Poľsko
	'PT', // Portugalsko
	'RO', // Rumunsko
	'SE', // Švédsko
	'SK', // Slovensko
	'SI', // Slovinsko
	'ES', // Španielsko
	'DE', // Nemecko
	'AT', // Rakúsko
	'GB' // Veľká Británia
] as const

export type EUCountryCode = (typeof EU_COUNTRIES)[number]

/**
 * Získa zoznam štátov Európskej únie s ich názvami v slovenčine
 * @returns Array objektov s id (ISO kód) a name (názov krajiny)
 */
export const getEUCountries = () => {
	return EU_COUNTRIES.map(countryCode => ({
		id: countryCode,
		name: countries.getName(countryCode, 'sk') || countryCode
	})).sort((a, b) => a.name.localeCompare(b.name, 'sk'))
}

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export const getReadTime = (wordCount: number | undefined) => {
	if (!wordCount) return 0
	const wordsPerMinute = 225 // Average adult reading speed
	return Math.ceil(wordCount / wordsPerMinute)
}

export const getFullName = (author: IEmployeeReduced | IEmployee) => {
	const prefix = author.titlePrefix ? `${author.titlePrefix} ` : ''
	const suffix = author.titleSuffix ? ` ${author.titleSuffix}` : ''
	return `${prefix}${author.firstName} ${author.lastName}${suffix}`
}

export const formatDate = (date: string | undefined) => {
	if (!date) return ''
	return format(date, 'dd.MM.yyyy')
}

export const formatDateTime = (date: string | undefined) => {
	if (!date) return ''
	return format(date, 'dd.MM.yyyy - HH:mm')
}

export const formatTime = (date: string | undefined) => {
	if (!date) return ''
	return format(date, 'HH:mm')
}

/**
 * Termín v štýle „15.2.2026 • 14:30“; s koncom slotu „15.2.2026 • 14:30 – 15:00“.
 */
export const formatSlotDateTimeRange = (start: string | undefined, end?: string | undefined) => {
	if (!start) return ''
	const datePart = format(start, 'd.M.yyyy')
	const startTime = format(start, 'HH:mm')
	if (!end) return `${datePart} • ${startTime}`
	return `${datePart} • ${startTime} – ${format(end, 'HH:mm')}`
}

export function normalize(str: string): string {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
}

export function setQueryParam(
	router: any,
	searchParams: URLSearchParams,
	key: string,
	value: string | null
) {
	const params = new URLSearchParams(Array.from(searchParams.entries()))
	if (value) {
		params.set(key, value)
	} else {
		params.delete(key)
	}
	router.replace(`?${params.toString()}`)
}

export function setMultipleQueryParams(
	router: any,
	searchParams: URLSearchParams,
	updates: Record<string, string | string[] | null>,
	pushToHistory = false
) {
	const params = new URLSearchParams(Array.from(searchParams.entries()))

	Object.entries(updates).forEach(([key, value]) => {
		// Delete existing params with this key first (including indexed variants)
		params.delete(key)
		// Also delete any indexed params like key[0], key[1], etc.
		Array.from(params.keys()).forEach(paramKey => {
			if (paramKey.startsWith(`${key}[`) && paramKey.endsWith(']')) {
				params.delete(paramKey)
			}
		})

		// Add new value(s)
		if (Array.isArray(value)) {
			// For arrays, add each value with indexed key
			value.forEach((v, index) => params.set(`${key}[${index}]`, v))
		} else if (value) {
			// For single values, set directly
			params.set(key, value)
		}
		// If null, it stays deleted
	})

	const method = pushToHistory ? 'push' : 'replace'
	router[method](`?${params.toString()}`)
}

/**
 * Získa názov jazyka v danom jazyku
 * @param code ISO kód jazyka
 * @param t_common funkcia pre preklady z common namespace
 * @returns Názov jazyka v danom jazyku
 */
export const getLocaleName = (code: string, t_common: (key: string) => string) => {
	const translated = t_common(`languages.${code}`)

	if (translated.includes('languages.')) {
		return code
	}

	return translated
}

export const getMinimumPatientAge = (
	minAge: number | null,
	maxAge: number | null,
	t_ageRange: (key: string, params?: Record<string, string>) => string
) => {
	// Age range not defined
	try {
		if (!minAge && !maxAge) {
			return t_ageRange('all')
		}

		if ((minAge === 0 && maxAge === 0) || (minAge === 0 && maxAge === null)) {
			return t_ageRange('all')
		}

		if ((minAge === 0 && maxAge === 100) || (minAge === 100 && maxAge === null)) {
			return t_ageRange('all')
		}

		// Age range defined only by min age
		if (minAge !== null && (maxAge === null || maxAge === 0)) {
			return minAge === 1
				? t_ageRange('minOne', { min: minAge.toString() })
				: t_ageRange('minMany', { min: minAge.toString() })
		}

		// Age range defined only by max age
		if ((minAge === null || minAge === 0) && maxAge !== null) {
			return t_ageRange('max', { max: maxAge.toString() })
		}

		// Age range defined by min and max age
		if (minAge !== null && maxAge !== null) {
			return t_ageRange('range', { min: minAge.toString(), max: maxAge.toString() })
		}

		return null
	} catch (error) {
		Sentry.captureException(error)
		return null
	}
}

export type FormatPriceOptions = {
	/** When the amount is a whole euro, omit the `,00` fraction (order-flow product display). */
	omitZeroFraction?: boolean
}

/** Pass to {@link formatPrice} for order-funnel UI (whole euros show without `,00`). */
export const orderFlowPriceFormatOptions: FormatPriceOptions = { omitZeroFraction: true }

function isWholeEuroAmount(value: number): boolean {
	return Math.abs(value - Math.round(value)) < 1e-6
}

export function formatPrice(price: number | null, options?: FormatPriceOptions) {
	const omit = Boolean(options?.omitZeroFraction)

	if (price === null) {
		return {
			price: omit ? '0' : '0,00',
			currency: '€'
		}
	}

	const useShortFraction = omit && isWholeEuroAmount(price)
	const formattedPrice = price.toLocaleString('sk-SK', {
		minimumFractionDigits: useShortFraction ? 0 : 2,
		maximumFractionDigits: 2
	})
	const currency = '€'

	return {
		price: formattedPrice,
		currency: currency
	}
}

/** Document Y position for scrolling: viewport-relative rect + current scroll (offsetTop is offset-parent-relative). */
function getScrollTopForElement(el: HTMLElement, offsetY: number): number {
	const rect = el.getBoundingClientRect()
	return rect.top + window.scrollY - offsetY
}

/** Options shared by {@link handleProgramaticScroll} and {@link scrollToElementById}. */
export type ProgrammaticScrollOptions = {
	/** When true (default), URL becomes `pathname#hash` without query string. */
	removeParams?: boolean
}

function applyProgrammaticScrollToElement(
	targetElement: HTMLElement,
	hash: string,
	removeParams: boolean
) {
	const fragment = normalizeChainedHashFragment(hash.replace(/^#/, ''))
	const headerHeight = (document.querySelector('header.header') as HTMLElement)?.offsetHeight || 0

	console.log({
		fragment,
		headerHeight,
		scrollTop: window.scrollY,
		targetElement: targetElement.getBoundingClientRect(),
		getScrollTopForElement: getScrollTopForElement(targetElement, headerHeight),
	})

	window.scrollTo({
		top: getScrollTopForElement(targetElement, headerHeight),
		behavior: 'smooth'
	})

	if (! removeParams) {
		return;
	}

	window.history.pushState(null, '', `${window.location.pathname}#${fragment}`)

	// `pushState` does not fire `hashchange`; nav active state listens for it.
	window.dispatchEvent(new Event('hashchange'))
}

/**
 * Scroll to an in-page element by its `id` (with or without a leading `#`) and update the URL hash
 * the same way as {@link handleProgramaticScroll} for anchor clicks.
 */
export function scrollToElementById(elementId: string, options?: ProgrammaticScrollOptions): void {
	const hash = normalizeChainedHashFragment(elementId.replace(/^#/, ''))
	if (!hash) return

	const targetElement = document.getElementById(hash)
	if (!targetElement) return

	applyProgrammaticScrollToElement(targetElement, hash, options?.removeParams ?? true)
}

export function handleProgramaticScroll(
	e: React.MouseEvent<HTMLAnchorElement>,
	removeParams = true
) {
	const href = e.currentTarget.href

	if (href && href.includes('#')) {
		const hashIndex = href.indexOf('#')
		const hash = normalizeChainedHashFragment(href.slice(hashIndex + 1))

		if (hash) {
			const targetElement = document.getElementById(hash)

			if (targetElement) {
				e.preventDefault()
				applyProgrammaticScrollToElement(targetElement, hash, removeParams)
			}
		}
	}
}
